/**
 * Shared composite bits for the account / admin / directory screens,
 * assembled ONLY from src/ui primitives + existing gate/community bits.
 * Includes the single moderation-list scaffold shared by the four admin
 * moderation screens (notices / polls / issues / vendors).
 */
import { router, usePathname } from 'expo-router';
import React from 'react';
import { Linking, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { type LoadState } from '@/components/gate/api';
import { InfoNote } from '@/components/gate/bits';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, EmptyState, Hairline, IconButton, Pill, Screen, Skeleton, TopBar } from '@/ui';

import { Icon, type IconName } from './icons';

const WIDE = 1024;

/** Back that survives cold web deep-links (no history → profile tab). */
export function goBack() {
  if (router.canGoBack()) router.back();
  else router.replace('/(tabs)/profile');
}

/** Dial a phone number; silently no-ops if the dialer is unavailable. */
export function callNumber(phone?: string) {
  const clean = (phone ?? '').replace(/[^+\d]/g, '');
  if (clean) void Linking.openURL(`tel:${clean}`).catch(() => {});
}

/** Round call affordance used on directory / emergency rows. */
export function CallIconButton({ phone, name, prominent }: {
  phone?: string; name: string; prominent?: boolean;
}) {
  const { colors } = useTheme();
  if (prominent) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Call ${name}`}
        onPress={() => callNumber(phone)}
        style={({ pressed }) => ({
          width: 44, height: 44, borderRadius: radius.full,
          alignItems: 'center', justifyContent: 'center',
          backgroundColor: pressed ? colors.primary600 : colors.primary500,
        })}
      >
        <Icon name="phone" size={18} color={colors.canvas0} />
      </Pressable>
    );
  }
  return (
    <IconButton label={`Call ${name}`} onPress={() => callNumber(phone)}>
      <Icon name="phone" size={18} color={colors.primary500} />
    </IconButton>
  );
}

/** Compact secondary action button for moderation rows (44pt touch target). */
export function SmallButton({ label, icon, danger, active, disabled, onPress }: {
  label: string; icon?: IconName; danger?: boolean; active?: boolean;
  disabled?: boolean; onPress?: () => void;
}) {
  const { colors } = useTheme();
  const fg = danger ? colors.danger500 : colors.textPrimary;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: !!active }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({
        minHeight: 44, paddingHorizontal: space.s3, borderRadius: radius.md,
        borderWidth: 1, borderColor: active ? colors.borderFirm : colors.border,
        backgroundColor: pressed || active ? colors.canvas1 : 'transparent',
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
        opacity: disabled ? 0.4 : 1,
      })}
    >
      {icon ? <Icon name={icon} size={15} color={fg} /> : null}
      <AppText variant="bodySm" weight="500" style={{ color: fg }}>{label}</AppText>
    </Pressable>
  );
}

/** Square icon tile (design .avatar-ic) with an optional tint pair. */
export function IconCircle({ name, size = 40, bg, fg }: {
  name: IconName; size?: number; bg?: string; fg?: string;
}) {
  const { colors } = useTheme();
  return (
    <View style={{
      width: size, height: size, borderRadius: radius.full,
      backgroundColor: bg ?? colors.canvas1, alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name={name} size={Math.round(size * 0.45)} color={fg ?? colors.textSecondary} />
    </View>
  );
}

/**
 * The ONE moderation-list scaffold: TopBar (+ optional create), skeletons
 * while loading, EmptyState on empty/error, gap-stacked rows when ready.
 * Desktop (≥1024, web): the same persistent AdminSidebar + content-pane
 * shell used by admin/index + admin/users, so notices/polls/issues/vendors/
 * approvals all get it for free (audit 05 P0-4).
 */
export function ModerationScreen({
  title, createLabel, onCreate, state, count, emptyIcon, emptyTitle, emptyBody, notice, children,
}: {
  title: string; createLabel?: string; onCreate?: () => void;
  state: LoadState; count: number;
  emptyIcon: IconName; emptyTitle: string; emptyBody: string;
  notice?: string | null; children?: React.ReactNode;
}) {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;

  const body = (
    <>
      {notice ? <InfoNote icon="info" style={{ marginBottom: space.s3 }}>{notice}</InfoNote> : null}
      {state === 'loading' ? (
        <View style={{ gap: space.s3 }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} h={104} />)}
        </View>
      ) : count === 0 ? (
        <EmptyState
          icon={<Icon name={emptyIcon} size={40} color={colors.textTertiary} />}
          title={state === 'error' ? `${title} unavailable` : emptyTitle}
          body={state === 'error' ? 'We could not load this right now. Try again in a bit.' : emptyBody}
        />
      ) : (
        <View style={{ gap: space.s3 }}>{children}</View>
      )}
    </>
  );

  if (wide) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
        <AdminSidebar />
        <View style={{ flex: 1 }}>
          <View style={{
            paddingHorizontal: space.s6, paddingVertical: space.s4, borderBottomWidth: 0.5,
            borderBottomColor: colors.borderSoft, flexDirection: 'row', alignItems: 'center', gap: space.s3,
          }}>
            <AppText variant="headingLg" style={{ flex: 1 }}>{title}</AppText>
            {onCreate ? <Button title={`+ ${createLabel ?? 'Create'}`} onPress={onCreate} /> : null}
          </View>
          <Screen padded style={{ paddingTop: space.s5, paddingHorizontal: space.s6 }}>
            {body}
          </Screen>
        </View>
      </View>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar
        title={title}
        onBack={goBack}
        right={onCreate ? (
          <IconButton label={createLabel ?? 'Create'} onPress={onCreate}>
            <Icon name="plus" size={22} color={colors.textPrimary} />
          </IconButton>
        ) : undefined}
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {body}
      </Screen>
    </Screen>
  );
}

/**
 * Generic admin screen chrome for screens that own their own body (loading /
 * error / form state) and just need the same shell ModerationScreen gives
 * the four moderation lists: TopBar on mobile, AdminSidebar + plain header on
 * desktop (≥1024, web). Used by the create-notice/create-poll/create-vendor
 * forms and admin/users/[id] (audit 05 P0-4 — desktop shell missing there too).
 */
export function AdminScreenShell({ title, right, children }: {
  title: string; right?: React.ReactNode; children: React.ReactNode;
}) {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;

  if (wide) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
        <AdminSidebar />
        <View style={{ flex: 1 }}>
          <View style={{
            paddingHorizontal: space.s6, paddingVertical: space.s4, borderBottomWidth: 0.5,
            borderBottomColor: colors.borderSoft, flexDirection: 'row', alignItems: 'center', gap: space.s3,
          }}>
            <AppText variant="headingLg" style={{ flex: 1 }}>{title}</AppText>
            {right}
          </View>
          <View style={{ flex: 1 }}>{children}</View>
        </View>
      </View>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title={title} onBack={goBack} right={right} />
      {children}
    </Screen>
  );
}

/* ---------------- Desktop admin console (Admin dashboard / Admin users frames) ---------------- */

export type AdminNavItem = { label: string; path: string; icon: IconName; countKey?: string };

/** Grouped nav — flat 14-item list was unscannable (user report 2026-07-18).
 * PEOPLE = accounts in/out · COMMUNITY = day-to-day publishing/moderation ·
 * GOVERNANCE = the committee/statutory layer · OVERSIGHT = control surfaces. */
export const ADMIN_NAV_GROUPS: { title?: string; items: AdminNavItem[] }[] = [
  { items: [{ label: 'Dashboard', path: '/admin', icon: 'home' }] },
  {
    title: 'People',
    items: [
      { label: 'Approvals', path: '/admin/approvals', icon: 'user-plus', countKey: 'pendingApprovals' },
      { label: 'Users', path: '/admin/users', icon: 'users' },
    ],
  },
  {
    title: 'Community',
    items: [
      { label: 'Notices', path: '/admin/notices', icon: 'megaphone' },
      { label: 'Polls', path: '/admin/polls', icon: 'list' },
      { label: 'Issues', path: '/admin/issues', icon: 'alert-circle' },
      { label: 'Vendors', path: '/admin/vendors', icon: 'wrench' },
      { label: 'Amenities', path: '/admin/amenities', icon: 'calendar', countKey: 'amenities' },
    ],
  },
  {
    title: 'Governance',
    items: [
      { label: 'Positions', path: '/admin/positions', icon: 'key' },
      { label: 'Meetings', path: '/meetings', icon: 'calendar' },
      { label: 'Compliance', path: '/compliance', icon: 'alert-circle' },
      { label: 'Elections', path: '/elections', icon: 'check' },
      { label: 'Finances', path: '/finances', icon: 'credit-card' },
      { label: 'Registers', path: '/registers', icon: 'file-text' },
    ],
  },
  {
    title: 'Oversight',
    items: [
      { label: 'Pending actions', path: '/pending-actions', icon: 'clock', countKey: 'pendingActions' },
      { label: 'Audit trail', path: '/admin/audit', icon: 'file-text' },
    ],
  },
];

/** Desktop (≥1024) admin console sidebar — persistent nav for admin/* on wide web. */
export function AdminSidebar({ counts }: { counts?: Record<string, number | undefined> }) {
  const { colors } = useTheme();
  const pathname = usePathname();
  const { user } = useSession();
  return (
    <View style={{ width: 248, borderRightWidth: 0.5, borderRightColor: colors.borderSoft, backgroundColor: colors.canvas0 }}>
      <View style={{ paddingHorizontal: space.s4, paddingVertical: space.s5 }}>
        <BrandLockup markHeight={26} />
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          {user?.role === 'MASTER_ADMIN' ? 'Master admin' : 'Committee admin'}
          {user?.fullName ? ` · ${user.fullName.split(' ')[0]}` : ''}
        </AppText>
      </View>
      <Hairline />
      {/* The nav outgrew short laptop viewports — it must scroll on its own,
       * with the back-to-app link pinned below (user report 2026-07-17). */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: space.s3, gap: 2 }}>
        {ADMIN_NAV_GROUPS.map((grp, gi) => (
          <View key={grp.title ?? `g${gi}`}>
            {grp.title ? (
              <AppText variant="label" tone="tertiary"
                style={{ marginTop: gi > 0 ? space.s4 : 0, marginBottom: 4, paddingHorizontal: space.s3 }}>
                {grp.title}
              </AppText>
            ) : null}
            {grp.items.map((n) => {
              const active = n.path === '/admin' ? pathname === '/admin' : pathname.startsWith(n.path);
              const badge = n.countKey ? counts?.[n.countKey] : undefined;
              return (
                <Pressable
                  key={n.path}
                  accessibilityRole="link"
                  onPress={() => router.push(n.path)}
                  style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
                    flexDirection: 'row', alignItems: 'center', gap: space.s3,
                    paddingVertical: 10, paddingHorizontal: space.s3, borderRadius: radius.md,
                    backgroundColor: active ? colors.primary100 : hovered || pressed ? colors.canvas1 : 'transparent',
                  })}
                >
                  <Icon name={n.icon} size={18} color={active ? colors.primary900 : colors.textSecondary} />
                  <AppText variant="bodyMd" weight={active ? '600' : '400'} style={{ color: active ? colors.primary900 : colors.textSecondary, flex: 1 }}>
                    {n.label}
                  </AppText>
                  {badge ? <Pill status="urgent" label={String(badge)} /> : null}
                </Pressable>
              );
            })}
          </View>
        ))}
      </ScrollView>
      <Hairline />
      {/* Admins are residents too — the console must always offer a way back
       * to the normal app (user request 2026-07-17). */}
      <View style={{ padding: space.s3 }}>
        <Pressable
          accessibilityRole="link"
          onPress={() => router.push('/(tabs)/home')}
          style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
            flexDirection: 'row', alignItems: 'center', gap: space.s3,
            paddingVertical: 10, paddingHorizontal: space.s3, borderRadius: radius.md,
            backgroundColor: hovered || pressed ? colors.canvas1 : 'transparent',
          })}
        >
          <Icon name="home" size={18} color={colors.textSecondary} />
          <AppText variant="bodyMd" style={{ color: colors.textSecondary }}>Back to resident app</AppText>
        </Pressable>
      </View>
    </View>
  );
}

/**
 * KPI stat card — Admin dashboard frame's top row. `highlight` reserves the
 * screen's single sanctioned amber moment (pass it on at most one card).
 */
export function KpiCard({ label, value, sub, highlight }: {
  label: string; value: string; sub?: string; highlight?: boolean;
}) {
  const { colors } = useTheme();
  return (
    <View style={{
      flex: 1, minWidth: 150, borderRadius: radius.lg, padding: space.s4,
      borderWidth: 1, borderColor: highlight ? colors.accent500 : colors.borderSoft,
      backgroundColor: highlight ? colors.accent100 : colors.canvas0,
    }}>
      <AppText variant="label" style={{ color: highlight ? colors.accent700 : colors.textTertiary }}>{label}</AppText>
      <AppText mono weight="500" style={{ fontSize: 26, lineHeight: 32, marginTop: space.s2 }}>{value}</AppText>
      {sub ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{sub}</AppText> : null}
    </View>
  );
}
